import classNames from 'classnames';
import { type JSX } from 'react';
import { useDiveContext } from '../../../context';
import { DiNav } from '../../DiNav/DiNav';
import { HeaderNavDropdown } from '../HeaderNavDropdown/HeaderNavDropdown';
import HeaderNavItem from '../HeaderNavItem/HeaderNavItem';
import { headerListCVA, headerNavCVA } from './HeaderNav.cva';
import type { HeaderNavProps } from './HeaderNav.types';

/**
 * Horizontal list of navigation items to be used in the header.
 * It can contain a mix of `HeaderNavItem` and `HeaderNavDropdown` depending on the `overflow` props.
 *
 * It can be used in two overflow behaviors:
 * - `'dropdown'`: Overflowing items are moved into a dropdown menu.
 * - `'scroll'`: Items remain on a single line and are horizontally scrollable.
 *
 * ### Accessibility:
 * - **ARIA:** `role="menubar"` on the `<ul />` and `role="menuitem"` on the items.
 * - **Tab and Shift+Tab:** `<ul />` is considered a single tab stop. Only the menubar itself is tabbable. The items are accessible via arrow keys.
 * - **Navigation keys:** ArrowKeys (`Up`, `Down`, `Left`, `Right`) to navigate between items inside.
 * - **Press:** (`Enter` or `Space`) to select/click an item.
 *
 * @example
 *
 * ```tsx
 * <HeaderNav
 *   items={menuItems}
 *   activeIndex={0} // first item is active
 *   dropdownLabel="More"
 *   overflow="scroll"
 * />
 * ```
 */
export function HeaderNav({
  className,
  isTransparent,
  overflow = 'dropdown',
  dropdownLabel = 'More',
  items,
  activeIndex,
  shouldCenterActiveItem = true,
  ...props
}: HeaderNavProps): JSX.Element {
  const { isTv, device } = useDiveContext();

  const hasOverflowGradients =
    !isTransparent && !isTv && !(overflow === 'dropdown');

  return (
    <DiNav
      className={classNames(headerNavCVA({ hasOverflowGradients }), className)}
      dropdownElement={
        // items will be dynamically overridden at runtime
        <HeaderNavDropdown items={[]} label={dropdownLabel} />
      }
      overflow={overflow}
      navListClassName={headerListCVA({ device })}
      role="menubar"
      activeIndex={activeIndex}
      shouldCenterActiveItem={shouldCenterActiveItem}
      {...props}
    >
      {items.map((navItem, idx) => {
        return (
          <HeaderNavItem
            outerWrapper={<li role="none" />}
            key={navItem.id ?? navItem.label}
            {...navItem}
            isCurrent={idx === activeIndex}
            font="hind"
          />
        );
      })}
    </DiNav>
  );
}
